import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, Stack, TextField, Typography
} from '@mui/material';
import {
  Add as AddIcon, EditOutlined as EditIcon, DeleteOutline as DeleteIcon,
  UploadFile as UploadIcon, DragIndicator as DragIcon
} from '@mui/icons-material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import AssignmentChip from 'components/nutrition/AssignmentChip';
import NutritionCardActions from 'components/nutrition/NutritionCardActions';
import AvatarCropModal from 'components/shared/modals/AvatarCropModal';
import BlockNoteContentEditor from 'components/shared/content/BlockNoteContentEditor';
import AssignDialog from 'components/nutrition/AssignDialog';
import RecipeCardTemplate from 'components/nutrition/RecipeCardTemplate';
import RecipeIllustration, { RECIPE_ILLUSTRATIONS } from 'components/nutrition/RecipeIllustration';
import RECIPE_COLORS, { DEFAULT_RECIPE_COLOR } from 'components/nutrition/recipeColors';
import { loadDraft, saveDraft, clearDraft } from 'common/utils/editorDraft';
import {
  RECIPE_BOOKS, CREATE_RECIPE_BOOK, UPDATE_RECIPE_BOOK, DELETE_RECIPE_BOOKS,
  CREATE_RECIPE, UPDATE_RECIPE, DELETE_RECIPES, REORDER_RECIPES
} from 'api/graphql/nutrition';

const PER_PAGE = 15;
// Nombres de archivo largos: trunca en el medio para que no desborde el botón.
const shortName = (n) => (n && n.length > 22 ? `${n.slice(0, 12)}…${n.slice(-7)}` : n);
// Estilo común de botón de subida: el texto se trunca con ellipsis y no empuja la caja.
const uploadBtnSx = { color: 'text.primary', borderColor: 'hairline.strong', textTransform: 'none', fontWeight: 700, maxWidth: '100%', '& .MuiButton-startIcon': { flexShrink: 0 }, '& > span:last-of-type': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } };

function RecipeEditor({ open, bookId, recipe = null, onClose, refetch }) {
  const { t } = useTranslation(['nutrition', 'buttons']);
  const isEdit = Boolean(recipe);
  const draftKey = `otx_nutri_recipe_${recipe?.id ?? `new_${bookId}`}`;
  const [draft] = useState(() => loadDraft(draftKey));
  const [name, setName] = useState(draft?.name ?? recipe?.name ?? '');
  const [difficulty, setDifficulty] = useState(draft?.difficulty ?? recipe?.difficulty ?? '');
  const [portions, setPortions] = useState(draft?.portions ?? recipe?.portions ?? '');
  const [ingredientsHtml, setIngredientsHtml] = useState(draft?.ingredientsHtml ?? recipe?.ingredients_html ?? '');
  const [stepsHtml, setStepsHtml] = useState(draft?.stepsHtml ?? recipe?.steps_html ?? '');
  const [mealLabel, setMealLabel] = useState(draft?.mealLabel ?? recipe?.meal_label ?? '');
  const [prepTime, setPrepTime] = useState(draft?.prepTime ?? recipe?.prep_time ?? '');
  const [footerNote, setFooterNote] = useState(draft?.footerNote ?? recipe?.footer_note ?? '');
  const [color, setColor] = useState(draft?.color ?? recipe?.accent_color ?? DEFAULT_RECIPE_COLOR);
  const [illustration, setIllustration] = useState(draft?.illustration ?? recipe?.illustration ?? '');
  const [image, setImage] = useState(null);
  const [background, setBackground] = useState(null);
  const [crop, setCrop] = useState({ open: false, src: null });
  const [createRecipe, { loading: creating }] = useMutation(CREATE_RECIPE);
  const [updateRecipe, { loading: updating }] = useMutation(UPDATE_RECIPE);

  // Borrador de la receta (texto): se limpia solo al guardar con éxito.
  useEffect(() => {
    saveDraft(draftKey, { name, difficulty, portions, ingredientsHtml, stepsHtml, mealLabel, prepTime, footerNote, color, illustration });
  }, [draftKey, name, difficulty, portions, ingredientsHtml, stepsHtml, mealLabel, prepTime, footerNote, color, illustration]);

  // Receta para la preview en vivo: usa los blobs locales recién elegidos o los guardados.
  const previewImage = useMemo(() => (image ? URL.createObjectURL(image) : recipe?.image_url ?? null), [image, recipe]);
  const previewBg = useMemo(() => (background ? URL.createObjectURL(background) : recipe?.background_image_url ?? null), [background, recipe]);
  const previewRecipe = {
    name: name || t('recipeName', { ns: 'nutrition', defaultValue: 'Nombre' }),
    image_url: previewImage,
    background_image_url: previewBg,
    ingredients_html: ingredientsHtml,
    steps_html: stepsHtml,
    portions: portions === '' ? null : Number(portions),
    accent_color: color,
    meal_label: mealLabel,
    prep_time: prepTime === '' ? null : Number(prepTime),
    footer_note: footerNote,
    illustration
  };

  const save = async () => {
    const base = {
      name: name.trim(),
      ingredients_html: ingredientsHtml,
      steps_html: stepsHtml,
      difficulty: difficulty || null,
      portions: portions === '' ? null : Number(portions),
      accent_color: color || null,
      meal_label: mealLabel.trim() || null,
      prep_time: prepTime === '' ? null : Number(prepTime),
      footer_note: footerNote.trim() || null,
      illustration: illustration || null
    };
    if (image) base.image = image;
    if (background) base.background = background;
    if (isEdit) await updateRecipe({ variables: { id: recipe.id, input: base } });
    else await createRecipe({ variables: { input: { recipe_book_id: bookId, ...base } } });
    clearDraft(draftKey);
    await refetch();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ sx: { backgroundColor: 'surface.content', backgroundImage: 'none', width: { xs: '100%', md: '94vw' }, maxWidth: 1440, m: { xs: 1, sm: 2 } } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>{isEdit ? t('editRecipe', { ns: 'nutrition', defaultValue: 'Editar receta' }) : t('newRecipe', { ns: 'nutrition', defaultValue: 'Nueva receta' })}</DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            <TextField label={t('recipeName', { ns: 'nutrition', defaultValue: 'Nombre' })} value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" />
            <TextField label={t('mealLabel', { ns: 'nutrition', defaultValue: 'Etiqueta (ej. Receta para almuerzos)' })} value={mealLabel} onChange={(e) => setMealLabel(e.target.value)} fullWidth size="small" />
            <Stack direction="row" spacing={2}>
              <TextField label={t('difficulty', { ns: 'nutrition', defaultValue: 'Dificultad' })} value={difficulty} onChange={(e) => setDifficulty(e.target.value)} fullWidth size="small" />
              <TextField label={t('portionsLabel', { ns: 'nutrition', defaultValue: 'Porciones' })} type="number" value={portions} onChange={(e) => setPortions(e.target.value)} fullWidth size="small" />
              <TextField label={t('prepTime', { ns: 'nutrition', defaultValue: 'Tiempo (min)' })} type="number" value={prepTime} onChange={(e) => setPrepTime(e.target.value)} fullWidth size="small" />
            </Stack>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>{t('ingredients', { ns: 'nutrition', defaultValue: 'Ingredientes' })}</Typography>
              <BlockNoteContentEditor value={ingredientsHtml} onChange={setIngredientsHtml} minHeight={150} lossless />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>{t('steps', { ns: 'nutrition', defaultValue: 'Preparación' })}</Typography>
              <BlockNoteContentEditor value={stepsHtml} onChange={setStepsHtml} minHeight={200} lossless />
            </Box>
            <TextField label={t('footerNote', { ns: 'nutrition', defaultValue: 'Nota de pie (ej. Receta de @cuenta)' })} value={footerNote} onChange={(e) => setFooterNote(e.target.value)} fullWidth size="small" />

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>{t('accentColor', { ns: 'nutrition', defaultValue: 'Color de acento' })}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {RECIPE_COLORS.map((c) => (
                  <Box
                    key={c.value}
                    role="button"
                    aria-label={c.name}
                    tabIndex={0}
                    onClick={() => setColor(c.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setColor(c.value); }}
                    sx={{
                      width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', backgroundColor: c.value,
                      outline: color === c.value ? '2px solid' : '1px solid',
                      outlineColor: color === c.value ? 'secondary.main' : 'hairline.strong',
                      outlineOffset: 2
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>{t('illustration', { ns: 'nutrition', defaultValue: 'Ilustración (opcional)' })}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {RECIPE_ILLUSTRATIONS.map((il) => (
                  <Box
                    key={il.key}
                    role="button"
                    aria-label={il.label}
                    title={il.label}
                    tabIndex={0}
                    onClick={() => setIllustration((prev) => (prev === il.key ? '' : il.key))}
                    onKeyDown={(e) => { if (e.key === 'Enter') setIllustration((prev) => (prev === il.key ? '' : il.key)); }}
                    sx={{
                      width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', borderRadius: '10px', color: illustration === il.key ? color : 'text.secondary',
                      border: '1px solid', borderColor: illustration === il.key ? 'secondary.main' : 'hairline.strong',
                      backgroundColor: illustration === il.key ? 'gold.soft' : 'transparent'
                    }}
                  >
                    <RecipeIllustration name={il.key} size={26} />
                  </Box>
                ))}
              </Stack>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button component="label" variant="outlined" color="inherit" startIcon={<UploadIcon />} sx={uploadBtnSx}>
                {image ? shortName(image.name) : t('attachImage', { ns: 'nutrition', defaultValue: 'Imagen' })}
                <input hidden type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) setCrop({ open: true, src: URL.createObjectURL(f) }); e.target.value = ''; }} />
              </Button>
              <Button component="label" variant="outlined" color="inherit" startIcon={<UploadIcon />} sx={uploadBtnSx}>
                {background ? shortName(background.name) : t('backgroundImage', { ns: 'nutrition', defaultValue: 'Imagen de fondo' })}
                <input hidden type="file" accept="image/*" onChange={(e) => setBackground(e.target.files?.[0] ?? null)} />
              </Button>
            </Stack>
          </Stack>

          {/* Preview en vivo de la tarjeta de receta (más ancha y pegada al hacer scroll). */}
          <Box sx={{ flex: 1.4, minWidth: { md: 560 }, alignSelf: 'flex-start', position: { md: 'sticky' }, top: { md: 0 } }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>{t('preview', { ns: 'nutrition', defaultValue: 'Vista previa' })}</Typography>
            <RecipeCardTemplate recipe={previewRecipe} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button color="inherit" onClick={onClose} sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>{t('cancel', { ns: 'buttons', defaultValue: 'Cancelar' })}</Button>
        <Button variant="contained" color="primary" onClick={save} disabled={creating || updating || !name.trim()} sx={{ textTransform: 'none', fontWeight: 700 }} startIcon={(creating || updating) ? <CircularProgress size={14} color="inherit" /> : null}>{t('save', { ns: 'buttons', defaultValue: 'Guardar' })}</Button>
      </DialogActions>

      {/* Recorte/encuadre de la foto de la receta (mismo cropper que avatar/portadas). */}
      <AvatarCropModal
        open={crop.open}
        imageSrc={crop.src}
        aspect={16 / 9}
        cropShape="rect"
        title={t('adjustImage', { ns: 'nutrition', defaultValue: 'Ajustar imagen' })}
        saveLabel={t('save', { ns: 'buttons', defaultValue: 'Guardar' })}
        onCancel={() => setCrop({ open: false, src: null })}
        onSave={(file) => { setImage(file); setCrop({ open: false, src: null }); }}
      />
    </Dialog>
  );
}
RecipeEditor.propTypes = { open: PropTypes.bool.isRequired, bookId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, recipe: PropTypes.object, onClose: PropTypes.func.isRequired, refetch: PropTypes.func.isRequired };

function SortableRecipeRow({ recipe, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: String(recipe.id) });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <Stack ref={setNodeRef} style={style} direction="row" alignItems="center" spacing={1} sx={{ p: 1, borderRadius: '10px', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.raised' }}>
      <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab', touchAction: 'none', color: 'text.disabled' }}><DragIcon fontSize="small" /></IconButton>
      <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }} noWrap>{recipe.name}</Typography>
      <IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton>
      <IconButton size="small" onClick={onDelete} sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" /></IconButton>
    </Stack>
  );
}
SortableRecipeRow.propTypes = { recipe: PropTypes.object.isRequired, onEdit: PropTypes.func.isRequired, onDelete: PropTypes.func.isRequired };

function RecipeBookEditor({ open, book = null, onClose, refetch }) {
  const { t } = useTranslation(['nutrition', 'buttons']);
  const isEdit = Boolean(book);
  const draftKey = `otx_nutri_book_${book?.id ?? 'new'}`;
  const [draft] = useState(() => loadDraft(draftKey));
  const [title, setTitle] = useState(draft?.title ?? book?.title ?? '');
  const [description, setDescription] = useState(draft?.description ?? book?.description ?? '');
  const [cover, setCover] = useState(null);
  const [recipes, setRecipes] = useState(book?.recipes ?? []);
  const [recipeEditor, setRecipeEditor] = useState({ open: false, recipe: null });
  const [delRecipe, setDelRecipe] = useState({ open: false, id: null });

  const [createBook, { loading: creating }] = useMutation(CREATE_RECIPE_BOOK);
  const [updateBook, { loading: updating }] = useMutation(UPDATE_RECIPE_BOOK);
  const [deleteRecipes] = useMutation(DELETE_RECIPES);
  const [reorderRecipes] = useMutation(REORDER_RECIPES);

  // Borrador del recetario (título/descripción): se limpia solo al guardar con éxito.
  useEffect(() => { saveDraft(draftKey, { title, description }); }, [draftKey, title, description]);

  const saveBook = async () => {
    const input = { title: title.trim(), description };
    if (cover) input.cover = cover;
    if (isEdit) await updateBook({ variables: { id: book.id, input } });
    else await createBook({ variables: { input } });
    clearDraft(draftKey);
    await refetch();
    onClose();
  };

  // Re-sincroniza la lista local con el servidor para que una receta recién creada/editada
  // aparezca de inmediato en el modal (sin tener que cerrar y reabrir).
  const syncRecipes = async () => {
    const res = await refetch();
    const fresh = res?.data?.recipeBooks?.data?.find((x) => String(x.id) === String(book?.id));
    if (fresh) setRecipes(fresh.recipes ?? []);
  };

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = recipes.findIndex((r) => String(r.id) === active.id);
    const newIndex = recipes.findIndex((r) => String(r.id) === over.id);
    const next = arrayMove(recipes, oldIndex, newIndex);
    setRecipes(next);
    await reorderRecipes({ variables: { recipe_book_id: book.id, ordered_ids: next.map((r) => String(r.id)) } });
  };

  const confirmDelRecipe = async () => {
    if (delRecipe.id) { await deleteRecipes({ variables: { id: [delRecipe.id] } }); await refetch(); setRecipes((prev) => prev.filter((r) => r.id !== delRecipe.id)); }
    setDelRecipe({ open: false, id: null });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { backgroundColor: 'surface.content', backgroundImage: 'none' } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>{isEdit ? t('editBook', { ns: 'nutrition', defaultValue: 'Editar recetario' }) : t('newBook', { ns: 'nutrition', defaultValue: 'Nuevo recetario' })}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField label={t('title', { ns: 'nutrition', defaultValue: 'Título' })} value={title} onChange={(e) => setTitle(e.target.value)} fullWidth size="small" />
          <TextField label={t('description', { ns: 'nutrition', defaultValue: 'Descripción' })} value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline minRows={2} />
          <Button component="label" variant="outlined" color="inherit" startIcon={<UploadIcon />} sx={{ ...uploadBtnSx, alignSelf: 'flex-start' }}>
            {cover ? shortName(cover.name) : t('attachCover', { ns: 'nutrition', defaultValue: 'Portada' })}
            <input hidden type="file" accept="image/*" onChange={(e) => setCover(e.target.files?.[0] ?? null)} />
          </Button>

          {isEdit && (
            <>
              <Divider />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{t('recipesSection', { ns: 'nutrition', defaultValue: 'Recetas' })}</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setRecipeEditor({ open: true, recipe: null })} sx={{ textTransform: 'none', fontWeight: 700, color: 'secondary.main' }}>{t('addRecipe', { ns: 'nutrition', defaultValue: 'Añadir receta' })}</Button>
              </Stack>
              <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={recipes.map((r) => String(r.id))} strategy={verticalListSortingStrategy}>
                  <Stack spacing={1}>
                    {recipes.map((r) => (
                      <SortableRecipeRow key={r.id} recipe={r} onEdit={() => setRecipeEditor({ open: true, recipe: r })} onDelete={() => setDelRecipe({ open: true, id: r.id })} />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
              {recipes.length === 0 && <Typography variant="caption" sx={{ color: 'text.disabled' }}>{t('noRecipesYet', { ns: 'nutrition', defaultValue: 'Sin recetas aún.' })}</Typography>}
            </>
          )}
          {!isEdit && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('saveBookFirst', { ns: 'nutrition', defaultValue: 'Guarda el recetario para añadir recetas.' })}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button color="inherit" onClick={onClose} sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>{t('close', { ns: 'buttons', defaultValue: 'Cerrar' })}</Button>
        <Button variant="contained" color="primary" onClick={saveBook} disabled={creating || updating || !title.trim()} sx={{ textTransform: 'none', fontWeight: 700 }} startIcon={(creating || updating) ? <CircularProgress size={14} color="inherit" /> : null}>{t('save', { ns: 'buttons', defaultValue: 'Guardar' })}</Button>
      </DialogActions>

      {recipeEditor.open && book && <RecipeEditor open={recipeEditor.open} bookId={book.id} recipe={recipeEditor.recipe} onClose={() => setRecipeEditor({ open: false, recipe: null })} refetch={syncRecipes} />}
      <ConfirmDeleteModal open={delRecipe.open} onClose={() => setDelRecipe({ open: false, id: null })} onConfirm={confirmDelRecipe} count={1} />
    </Dialog>
  );
}
RecipeBookEditor.propTypes = { open: PropTypes.bool.isRequired, book: PropTypes.object, onClose: PropTypes.func.isRequired, refetch: PropTypes.func.isRequired };

function NutritionRecipeBooks() {
  const { t } = useTranslation(['nutrition']);
  const { data, loading, refetch } = useQuery(RECIPE_BOOKS, { variables: { first: PER_PAGE, page: 1, search: '' }, fetchPolicy: 'cache-and-network' });
  const [deleteBooks] = useMutation(DELETE_RECIPE_BOOKS);
  const [editor, setEditor] = useState({ open: false, book: null });
  const [del, setDel] = useState({ open: false, id: null });
  const [assign, setAssign] = useState({ open: false, bookId: null, assignments: [] });

  const rows = data?.recipeBooks?.data ?? [];

  const confirmDelete = async () => {
    if (del.id) await deleteBooks({ variables: { id: [del.id] } });
    setDel({ open: false, id: null });
    refetch();
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setEditor({ open: true, book: null })} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {t('newBook', { ns: 'nutrition', defaultValue: 'Nuevo recetario' })}
        </Button>
      </Stack>

      {loading && rows.length === 0 && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: 'gold.main' }} /></Box>}
      {!loading && rows.length === 0 && <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>{t('noBooksAdmin', { ns: 'nutrition', defaultValue: 'No hay recetarios. Crea el primero.' })}</Box>}

      <Stack spacing={1.5}>
        {rows.map((b) => (
          <Stack key={b.id} direction="row" alignItems="center" spacing={1.5} sx={{ p: 2, borderRadius: '14px', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.content' }}>
            {/* Título en su propia línea y, debajo, destino y número de recetas. Compartiendo fila
                con tres botones el nombre del recetario se quedaba sin ancho en móvil. */}
            <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>{b.title}</Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                <AssignmentChip assignments={b.assignments_info} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{(b.recipes?.length ?? 0)} {t('recipesCount', { ns: 'nutrition', defaultValue: 'recetas' })}</Typography>
              </Stack>
            </Box>
            <NutritionCardActions
              onAssign={() => setAssign({ open: true, bookId: b.id, assignments: b.assignments_info ?? [] })}
              onEdit={() => setEditor({ open: true, book: b })}
              onDelete={() => setDel({ open: true, id: b.id })}
            />
          </Stack>
        ))}
      </Stack>

      {editor.open && <RecipeBookEditor open={editor.open} book={editor.book} onClose={() => { setEditor({ open: false, book: null }); refetch(); }} refetch={refetch} />}
      {assign.open && <AssignDialog open={assign.open} recipeBookId={assign.bookId} assignments={assign.assignments} onClose={(changed) => { setAssign({ open: false, bookId: null, assignments: [] }); if (changed) refetch(); }} />}
      <ConfirmDeleteModal open={del.open} onClose={() => setDel({ open: false, id: null })} onConfirm={confirmDelete} count={1} />
    </Box>
  );
}

export default NutritionRecipeBooks;
